import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";

export function useServerCountdown(targetIso) {
  const { serverNow } = useApp();
  const [now, setNow] = useState(() => serverNow());
  useEffect(() => {
    const t = setInterval(() => setNow(serverNow()), 1000);
    return () => clearInterval(t);
  }, [serverNow]);
  if (!targetIso) return null;
  const diff = new Date(targetIso).getTime() - now;
  return Math.max(0, diff);
}

export function formatDuration(ms) {
  if (ms == null) return "--";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  if (d > 0) return { d: pad(d), h: pad(h), m: pad(m), s: pad(sec), hasDays: true };
  return { d: "00", h: pad(h), m: pad(m), s: pad(sec), hasDays: false };
}

export function formatIST(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour: "numeric", minute: "2-digit", hour12: true, day: "numeric", month: "short" });
  } catch {
    return iso;
  }
}

export function formatClock(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "numeric", minute: "2-digit", hour12: true });
  } catch {
    return iso;
  }
}

export default function Countdown({ targetIso, label, liveLabel, compact = false }) {
  const ms = useServerCountdown(targetIso);
  if (ms === null) return null;
  if (ms <= 0) {
    return (
      <div data-testid="countdown-live" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#138808]/10 border border-[#138808]/30">
        <span className="w-2.5 h-2.5 rounded-full bg-[#138808] animate-pulse" />
        <span className="font-heading font-bold text-[#138808] tracking-wide">{liveLabel || "LIVE NOW"}</span>
      </div>
    );
  }
  const t = formatDuration(ms);
  const Box = ({ v, l }) => (
    <div className={`flex flex-col items-center ${compact ? "px-2" : "px-3 md:px-4"}`}>
      <span className={`font-timer font-bold text-slate-900 ${compact ? "text-2xl" : "text-4xl md:text-6xl"}`}>{v}</span>
      <span className="text-[10px] md:text-xs font-bold tracking-[0.2em] text-slate-400 uppercase">{l}</span>
    </div>
  );
  return (
    <div data-testid="countdown-timer" className="flex flex-col items-center gap-3">
      {label && <span className="text-xs font-bold tracking-[0.3em] text-slate-500 uppercase">{label}</span>}
      <div className="flex items-center divide-x divide-slate-200 bg-white/80 backdrop-blur-xl border border-slate-200 rounded-2xl py-3 md:py-4 shadow-sm">
        {t.hasDays && <Box v={t.d} l="Days" />}
        <Box v={t.h} l="Hours" />
        <Box v={t.m} l="Mins" />
        <Box v={t.s} l="Secs" />
      </div>
    </div>
  );
}
